import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Role {
  id: string;
  name: string;
  displayName: string;
  permissions: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roleId: string;
  role: Role;
  status: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  login: (user: User, accessToken: string, refreshToken?: string) => void;
  logout: () => void;
  updateTokens: (accessToken: string, refreshToken?: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      login: (user, accessToken) =>
        set({ user, accessToken, refreshToken: null, isAuthenticated: true }),
      logout: () =>
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false }),
      updateTokens: (accessToken) =>
        set(() => ({
          accessToken,
          // Refresh token lives only in the httpOnly cookie.
          refreshToken: null,
        })),
    }),
    {
      name: 'hepaderm-auth',
      // Tokens stay in memory only; refresh lives in an httpOnly cookie.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // Strip any tokens that older builds wrote to localStorage.
      migrate: (persistedState) => {
        if (persistedState && typeof persistedState === 'object') {
          const next = { ...(persistedState as Record<string, unknown>) };
          delete next.accessToken;
          delete next.refreshToken;
          return next;
        }
        return persistedState;
      },
    }
  )
);
